import {
    AfterViewInit,
    Component,
    OnInit,
    ViewChild,
    ChangeDetectionStrategy,
} from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { FormGroupType } from '../../../../core/models/form-group-type';
import { GoApplicationPreparationBaseComponent } from '../shared/components/go-application-preparation-base/go-application-preparation-base.component';
import { GoParametersFormModel } from './models/go-parameters-form-model';
import { GoApplicationPurposeSelectionComponent } from '../shared/components/go-application-purpose-selection/go-application-purpose-selection.component';
import { Router, RouterModule } from '@angular/router';
import { GoSegmentedToggleComponent } from './components/go-segmented-toggle/go-segmented-toggle.component';
import {
    takeUntil,
    tap,
    merge,
    distinctUntilChanged,
    debounceTime,
    firstValueFrom,
    map,
    catchError,
    of,
} from 'rxjs';
import { GoSegmentedToggleOption } from './components/go-segmented-toggle/models/go-segmented-toggle-option';
import {
    GoDataDirectionType,
    GoDataDirectionTypeToLabelMapping,
} from './models/go-data-direction-type';
import {
    GoFrequencyType,
    GoFrequencyTypeToLabelMapping,
} from './models/go-frequency-type';
import {
    GoLocationType,
    GoLocationTypeToLabelMapping,
} from './models/go-location-type';
import {
    GoStationClassType,
    GoStationClassTypeToLabelMapping,
} from './models/go-station-class-type';
import {
    MatExpansionModule,
    MatExpansionPanel,
} from '@angular/material/expansion';
import {
    GoCoordinatesType,
    GoCoordinatesTypeToLabelMapping,
} from './models/go-coordinates-type';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatFormFieldModule } from '@angular/material/form-field';
import { GoApplicationPreparationService } from '../shared/services/go-application-preparation/go-application-preparation.service';
import { ApplicationPageFacadeService } from '../../direct-entry/shared/services/application-page-facade/application-page-facade.service';
import { LatitudeLongitudeFormComponent } from '../../shared/components/latitude-longitude-form/latitude-longitude-form.component';
import {
    GoAntennaHeightUnitType,
    GoAntennaHeightUnitTypeToLabelMapping,
} from './models/go-antenna-height-unit-type';
import { IMaskDirective } from 'angular-imask';
import { MatInputModule } from '@angular/material/input';
import { SchedulesDAndHMaskConfig } from '../../shared/constants/schedules-d-and-h-mask-config';
import { FrequencyMaskConfig } from '../../shared/constants/frequency-mask-config';
import {
    GoEmissionDesignatorPowerType,
    GoEmissionDesignatorPowerTypeToLabelMapping,
} from './models/go-emission-designator-power-type';
import {
    GoEmissionDesignatorInformationType,
    GoEmissionDesignatorInformationTypeToLabelMapping,
} from './models/go-emission-designator-information-type';
import { GoApplicationSummaryService } from '../shared/services/go-application-summary/go-application-summary.service';
import { GoApplicationService } from '../shared/services/go-application/go-application.service';
import { GoMapComponent } from '../shared/components/go-map/go-map.component';
import { Coordinates } from '../shared/components/go-map/types/go-map-types';
import { GeoCoordinateSystemService } from '../../../../core/services/geo-coordinate-system/geo-coordinate-system.service';
import { GoParametersMappingService } from './services/go-parameters-mapping/go-parameters-mapping.service';
import { GoApplicationRequest } from '../shared/models/go-application-request';
import { GoStorageService } from './services/go-storage/go-storage.service';
import { tryParseToNumber } from '../../../../core/functions/string-helpers';
import { isEqual } from 'lodash-es';
import { ULSService } from '../../../../core/services/uls/uls.service';
import { GoParametersValidationMessages } from './constants/go-parameters-validation-messages';
import { HoverTooltipComponent } from '../../../../shared/components/tooltips/hover-tooltip/hover-tooltip.component';
import { GoParametersTextConstants } from './constants/go-parameters-text-constants';

@Component({
    selector: 'app-go-parameters',
    imports: [
        GoApplicationPurposeSelectionComponent,
        RouterModule,
        MatExpansionModule,
        ReactiveFormsModule,
        GoSegmentedToggleComponent,
        MatCheckboxModule,
        MatFormFieldModule,
        LatitudeLongitudeFormComponent,
        GoMapComponent,
        MatInputModule,
        IMaskDirective,
        HoverTooltipComponent,
    ],
    templateUrl: './go-parameters.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrls: ['../shared/styles/go-mat-panel.scss'],
})
export class GoParametersComponent
    extends GoApplicationPreparationBaseComponent<GoParametersFormModel>
    implements OnInit, AfterViewInit
{
    form: FormGroup<FormGroupType<GoParametersFormModel>> =
        this.getFormDefinition();

    getFormDefinition(): FormGroup<FormGroupType<GoParametersFormModel>> {
        const goApplicationRequestFormDefinition =
            this.goApplicationPreparationService.getFormDefinition();

        return new FormGroup<FormGroupType<GoParametersFormModel>>({
            applicationPurpose:
                goApplicationRequestFormDefinition.controls.applicationPurpose,
            frequencyType:
                goApplicationRequestFormDefinition.controls.frequencyType,
            locationType:
                goApplicationRequestFormDefinition.controls.locationType,
            stationClassType:
                goApplicationRequestFormDefinition.controls.stationClassType,
            dataDirectionType:
                goApplicationRequestFormDefinition.controls.dataDirectionType,
            numberOfFrequencies:
                goApplicationRequestFormDefinition.controls.numberOfFrequencies,
            coordinatesType:
                goApplicationRequestFormDefinition.controls.coordinatesType,
            latitude: goApplicationRequestFormDefinition.controls.latitude,
            longitude: goApplicationRequestFormDefinition.controls.longitude,
            towerRegistrationNumber:
                goApplicationRequestFormDefinition.controls
                    .towerRegistrationNumber,
            antennaHeightUnitType:
                goApplicationRequestFormDefinition.controls
                    .antennaHeightUnitType,
            heightToTip:
                goApplicationRequestFormDefinition.controls.heightToTip,
            fixedErp: goApplicationRequestFormDefinition.controls.fixedErp,
            mobileErp: goApplicationRequestFormDefinition.controls.mobileErp,
            emissionDesignatorPowerType:
                goApplicationRequestFormDefinition.controls
                    .emissionDesignatorPowerType,
            emissionDesignatorInformationType:
                goApplicationRequestFormDefinition.controls
                    .emissionDesignatorInformationType,
        });
    }

    hasChanges = false;
    goCoordinatesType = GoCoordinatesType;
    schedulesDAndHMaskConfig = SchedulesDAndHMaskConfig;
    frequencyMaskConfig = FrequencyMaskConfig;
    showAsrInvalidMsg = false;
    goParametersValidationMessages = GoParametersValidationMessages;
    readonly erpTooltip: string = GoParametersTextConstants.erpTooltip;

    @ViewChild('frequencyTypeExpansionPanel')
    frequencyTypeExpansionPanel!: MatExpansionPanel;

    @ViewChild('locationTypeExpansionPanel')
    locationTypeExpansionPanel!: MatExpansionPanel;

    @ViewChild('stationClassTypeExpansionPanel')
    stationClassTypeExpansionPanel!: MatExpansionPanel;

    @ViewChild('numberOfFrequenciesExpansionPanel')
    numberOfFrequenciesExpansionPanel!: MatExpansionPanel;

    @ViewChild('coordinatesExpansionPanel')
    coordinatesExpansionPanel!: MatExpansionPanel;

    @ViewChild('antennaHeightExpansionPanel')
    antennaHeightExpansionPanel!: MatExpansionPanel;

    @ViewChild('erpExpansionPanel')
    erpExpansionPanel!: MatExpansionPanel;

    @ViewChild('emissionDesignatorExpansionPanel')
    emissionDesignatorExpansionPanel!: MatExpansionPanel;

    @ViewChild(GoSegmentedToggleComponent)
    frequencyTypeSegmentedToggleComponent!: GoSegmentedToggleComponent<GoFrequencyType>;

    @ViewChild(GoSegmentedToggleComponent)
    locationTypeSegmentedToggleComponent!: GoSegmentedToggleComponent<GoLocationType>;

    @ViewChild(GoSegmentedToggleComponent)
    stationClassTypeSegmentedToggleComponent!: GoSegmentedToggleComponent<GoStationClassType>;

    @ViewChild(GoSegmentedToggleComponent)
    dataDirectionTypeSegmentedToggleComponent!: GoSegmentedToggleComponent<GoDataDirectionType>;

    @ViewChild(GoSegmentedToggleComponent)
    numberOfFrequenciesSegmentedToggleComponent!: GoSegmentedToggleComponent<number>;

    @ViewChild(GoSegmentedToggleComponent)
    coordinatesSegmentedToggleComponent!: GoSegmentedToggleComponent<GoCoordinatesType>;

    @ViewChild(GoSegmentedToggleComponent)
    antennaHeightUnitTypeSegmentedToggleComponent!: GoSegmentedToggleComponent<GoAntennaHeightUnitType>;

    @ViewChild(GoSegmentedToggleComponent)
    emissionDesignatorPowerTypeSegmentedToggleComponent!: GoSegmentedToggleComponent<
        GoEmissionDesignatorPowerType[]
    >;

    @ViewChild(GoSegmentedToggleComponent)
    emissionDesignatorInformationTypeSegmentedToggleComponent!: GoSegmentedToggleComponent<GoEmissionDesignatorInformationType>;

    readonly frequencyTypeSegmentedToggleOptions: GoSegmentedToggleOption<GoFrequencyType>[] =
        [
            {
                id: 'vhf',
                value: GoFrequencyType.VHF,
                label: GoFrequencyTypeToLabelMapping[GoFrequencyType.VHF],
            },
            {
                id: 'uhf',
                value: GoFrequencyType.UHF,
                label: GoFrequencyTypeToLabelMapping[GoFrequencyType.UHF],
            },
        ];

    readonly locationTypeSegmentedToggleOptions: GoSegmentedToggleOption<GoLocationType>[] =
        [
            {
                id: 'fixed',
                value: GoLocationType.Fixed,
                label: GoLocationTypeToLabelMapping[GoLocationType.Fixed],
            },
            {
                id: 'mobile-only',
                value: GoLocationType.MobileOnly,
                label: GoLocationTypeToLabelMapping[GoLocationType.MobileOnly],
            },
        ];

    readonly stationClassTypeSegmentedToggleOptions: GoSegmentedToggleOption<GoStationClassType>[] =
        [
            {
                id: 'shared',
                value: GoStationClassType.Shared,
                label: GoStationClassTypeToLabelMapping[
                    GoStationClassType.Shared
                ],
                description: 'Conventional (IG) or Trunked (YG)',
            },
            {
                id: 'exclusive',
                value: GoStationClassType.Exclusive,
                label: GoStationClassTypeToLabelMapping[
                    GoStationClassType.Exclusive
                ],
                description: 'Trunked (YG)',
                isDisabled:
                    this.isExclusiveStationClassOptionDisabled.bind(this),
            },
        ];

    readonly dataDirectionTypeSegmentedToggleOptions: GoSegmentedToggleOption<GoDataDirectionType>[] =
        [
            {
                id: 'simplex',
                value: GoDataDirectionType.Simplex,
                label: GoDataDirectionTypeToLabelMapping[
                    GoDataDirectionType.Simplex
                ],
            },
            {
                id: 'duplex',
                value: GoDataDirectionType.Duplex,
                label: GoDataDirectionTypeToLabelMapping[
                    GoDataDirectionType.Duplex
                ],
            },
        ];

    readonly numberOfFrequenciesSegmentedToggleOptions: GoSegmentedToggleOption<number>[] =
        [
            { id: 'one', value: 1, label: '1' },
            { id: 'two', value: 2, label: '2' },
            { id: 'three', value: 3, label: '3' },
            { id: 'four', value: 4, label: '4' },
            { id: 'five', value: 5, label: '5' },
        ];

    readonly coordinatesSegmentedToggleOptions: GoSegmentedToggleOption<GoCoordinatesType>[] =
        [
            {
                id: 'asr',
                value: GoCoordinatesType.ASR,
                label: GoCoordinatesTypeToLabelMapping[GoCoordinatesType.ASR],
            },
            {
                id: 'lat-long',
                value: GoCoordinatesType.LatitudeLongitude,
                label: GoCoordinatesTypeToLabelMapping[
                    GoCoordinatesType.LatitudeLongitude
                ],
            },
            {
                id: 'map',
                value: GoCoordinatesType.Map,
                label: GoCoordinatesTypeToLabelMapping[GoCoordinatesType.Map],
            },
        ];

    readonly antennaHeightUnitTypeSegmentedToggleOptions: GoSegmentedToggleOption<GoAntennaHeightUnitType>[] =
        [
            {
                id: 'meters',
                value: GoAntennaHeightUnitType.Meters,
                label: GoAntennaHeightUnitTypeToLabelMapping[
                    GoAntennaHeightUnitType.Meters
                ],
            },
            {
                id: 'feet',
                value: GoAntennaHeightUnitType.Feet,
                label: GoAntennaHeightUnitTypeToLabelMapping[
                    GoAntennaHeightUnitType.Feet
                ],
            },
        ];

    readonly emissionDesignatorPowerTypeSegmentedToggleOptions: GoSegmentedToggleOption<GoEmissionDesignatorPowerType>[] =
        [
            {
                id: 'four-k',
                value: GoEmissionDesignatorPowerType.FourK,
                label: GoEmissionDesignatorPowerTypeToLabelMapping[
                    GoEmissionDesignatorPowerType.FourK
                ],
            },
            {
                id: 'seven-k',
                value: GoEmissionDesignatorPowerType.SevenK,
                label: GoEmissionDesignatorPowerTypeToLabelMapping[
                    GoEmissionDesignatorPowerType.SevenK
                ],
            },
            {
                id: 'eight-k',
                value: GoEmissionDesignatorPowerType.EightK,
                label: GoEmissionDesignatorPowerTypeToLabelMapping[
                    GoEmissionDesignatorPowerType.EightK
                ],
            },
            {
                id: 'eleven-k',
                value: GoEmissionDesignatorPowerType.ElevenK,
                label: GoEmissionDesignatorPowerTypeToLabelMapping[
                    GoEmissionDesignatorPowerType.ElevenK
                ],
            },
        ];

    readonly emissionDesignatorInformationTypeSegmentedToggleOptions: GoSegmentedToggleOption<GoEmissionDesignatorInformationType>[] =
        [
            {
                id: 'data',
                value: GoEmissionDesignatorInformationType.Data,
                label: GoEmissionDesignatorInformationTypeToLabelMapping[
                    GoEmissionDesignatorInformationType.Data
                ],
            },
            {
                id: 'voice',
                value: GoEmissionDesignatorInformationType.Voice,
                label: GoEmissionDesignatorInformationTypeToLabelMapping[
                    GoEmissionDesignatorInformationType.Voice
                ],
            },
            {
                id: 'data-and-voice',
                value: GoEmissionDesignatorInformationType.DataAndVoice,
                label: GoEmissionDesignatorInformationTypeToLabelMapping[
                    GoEmissionDesignatorInformationType.DataAndVoice
                ],
            },
        ];

    constructor(
        protected override readonly goApplicationPreparationService: GoApplicationPreparationService,
        protected override readonly applicationPageFacadeService: ApplicationPageFacadeService,
        protected override readonly goApplicationService: GoApplicationService,
        protected override readonly router: Router,
        protected override readonly goStorageService: GoStorageService,
        private readonly goApplicationSummaryService: GoApplicationSummaryService,
        private readonly geoCoordinateSystemService: GeoCoordinateSystemService,
        private readonly goParametersMappingService: GoParametersMappingService,
        private readonly ulsService: ULSService,
    ) {
        super(
            goApplicationPreparationService,
            applicationPageFacadeService,
            goApplicationService,
            router,
            goStorageService,
        );
    }

    get frequencyTypeControl(): FormControl<GoFrequencyType | null> {
        return this.form.controls.frequencyType;
    }

    get locationTypeControl(): FormControl<GoLocationType | null> {
        return this.form.controls.locationType;
    }

    get stationClassTypeControl(): FormControl<GoStationClassType | null> {
        return this.form.controls.stationClassType;
    }

    get dataDirectionTypeControl(): FormControl<GoDataDirectionType | null> {
        return this.form.controls.dataDirectionType;
    }

    get numberOfFrequenciesControl(): FormControl<number | null> {
        return this.form.controls.numberOfFrequencies;
    }

    get coordinatesTypeControl(): FormControl<GoCoordinatesType | null> {
        return this.form.controls.coordinatesType;
    }

    get latitudeControl(): FormControl<string | null> {
        return this.form.controls.latitude;
    }

    get longitudeControl(): FormControl<string | null> {
        return this.form.controls.longitude;
    }

    get asrNumberControl(): FormControl<string | null> {
        return this.form.controls.towerRegistrationNumber;
    }

    get antennaHeightUnitTypeControl(): FormControl<GoAntennaHeightUnitType | null> {
        return this.form.controls.antennaHeightUnitType;
    }

    get heightToTipControl(): FormControl<string | null> {
        return this.form.controls.heightToTip;
    }

    get fixedErpControl(): FormControl<string | null> {
        return this.form.controls.fixedErp;
    }

    get mobileErpControl(): FormControl<string | null> {
        return this.form.controls.mobileErp;
    }

    get emissionDesignatorPowerTypeControl(): FormControl<
        GoEmissionDesignatorPowerType[] | null
    > {
        return this.form.controls.emissionDesignatorPowerType;
    }

    get emissionDesignatorInformationTypeControl(): FormControl<GoEmissionDesignatorInformationType | null> {
        return this.form.controls.emissionDesignatorInformationType;
    }

    get hasFrequencyTypeSelection(): boolean {
        return this.frequencyTypeControl.value !== null;
    }

    get hasLocationTypeSelection(): boolean {
        return this.locationTypeControl.value !== null;
    }

    get hasStationClassTypeSelection(): boolean {
        return this.stationClassTypeControl.value !== null;
    }

    get hasNumberOfFrequenciesSelection(): boolean {
        return this.numberOfFrequenciesControl.value !== null;
    }

    get hasCoordinatesTypeSelection(): boolean {
        return this.coordinatesTypeControl.value !== null;
    }

    get hasAntennaHeightUnitTypeSelection(): boolean {
        return this.heightToTipControl.value !== null;
    }

    get hasMinimumErpSelection(): boolean {
        return (
            this.fixedErpControl.value !== null ||
            this.mobileErpControl.value !== null
        );
    }

    get hasEmissionDesignatorSelection(): boolean {
        return (
            this.emissionDesignatorPowerTypeControl.value !== null ||
            this.emissionDesignatorInformationTypeControl.value !== null
        );
    }

    isFrequencyTypeSectionInvalid(): boolean {
        return (
            this.frequencyTypeControl.invalid &&
            this.frequencyTypeControl.touched
        );
    }

    isLocationTypeSectionInvalid(): boolean {
        return (
            this.locationTypeControl.invalid && this.locationTypeControl.touched
        );
    }

    isStationClassSectionInvalid(): boolean {
        return (
            (this.stationClassTypeControl.invalid &&
                this.stationClassTypeControl.touched) ||
            (this.dataDirectionTypeControl.invalid &&
                this.dataDirectionTypeControl.touched)
        );
    }

    isNumberOfFrequenciesSectionInvalid(): boolean {
        return (
            this.numberOfFrequenciesControl.invalid &&
            this.numberOfFrequenciesControl.touched
        );
    }

    isCoordinatesSectionInvalid(): boolean {
        return (
            (this.coordinatesTypeControl.invalid &&
                this.coordinatesTypeControl.touched) ||
            (this.latitudeControl.invalid && this.latitudeControl.touched) ||
            (this.longitudeControl.invalid && this.longitudeControl.touched) ||
            (this.asrNumberControl.invalid && this.asrNumberControl.touched)
        );
    }

    isAntennaHeightUnitTypeSectionInvalid(): boolean {
        return (
            (this.antennaHeightUnitTypeControl.invalid &&
                this.antennaHeightUnitTypeControl.touched) ||
            (this.heightToTipControl.invalid && this.heightToTipControl.touched)
        );
    }

    isMinimumErpRequiredSectionInvalid(): boolean {
        return (
            (this.fixedErpControl.invalid && this.fixedErpControl.touched) ||
            (this.mobileErpControl.invalid && this.mobileErpControl.touched)
        );
    }

    isEmissionDesignatorSectionInvalid(): boolean {
        return (
            (this.emissionDesignatorPowerTypeControl.invalid &&
                this.emissionDesignatorPowerTypeControl.touched) ||
            (this.emissionDesignatorInformationTypeControl.invalid &&
                this.emissionDesignatorInformationTypeControl.touched)
        );
    }

    getFrequencyTypeExpansionPanelTitle(): string {
        return this.goApplicationSummaryService.getFrequencyTypeTitle();
    }

    getFrequencyTypeExpansionPanelDescription(): string | null {
        return this.goApplicationSummaryService.getFrequencyTypeValueSummary(
            this.frequencyTypeControl.value,
        );
    }

    getLocationTypeExpansionPanelTitle(): string {
        return this.goApplicationSummaryService.getLocationTypeTitle();
    }

    getLocationTypeExpansionPanelDescription(): string | null {
        return this.goApplicationSummaryService.getLocationTypeValueSummary(
            this.locationTypeControl.value,
        );
    }

    getStationClassExpansionPanelTitle(): string {
        return this.goApplicationSummaryService.getStationClassTitle();
    }

    getStationClassExpansionPanelDescription(): string | null {
        return this.goApplicationSummaryService.getStationClassValueSummary(
            this.stationClassTypeControl.value,
            this.dataDirectionTypeControl.value,
            ' | ',
        );
    }

    getNumberOfFrequenciesExpansionPanelTitle(): string {
        return this.goApplicationSummaryService.getNumberOfFrequenciesTitle();
    }

    getNumberOfFrequenciesExpansionPanelDescription(): string | null {
        return this.goApplicationSummaryService.getNumberOfFrequenciesValueSummary(
            this.numberOfFrequenciesControl.value,
        );
    }

    getCoordinatesExpansionPanelTitle(): string {
        return this.goApplicationSummaryService.getCoordinatesTitle();
    }

    getCoordinatesExpansionPanelDescription(): string | null {
        return this.goApplicationSummaryService.getCoordinatesValueSummary(
            this.latitudeControl.value
                ? tryParseToNumber(this.latitudeControl.value)
                : null,
            this.longitudeControl.value
                ? tryParseToNumber(this.longitudeControl.value)
                : null,
            ' / ',
        );
    }

    getCoordinatesExpansionPanelDescriptionAsr(): string | null {
        return this.goApplicationSummaryService.getCoordinatesValuePanelCollapsedAsr(
            this.asrNumberControl.value ?? undefined,
        );
    }

    getAntennaHeightExpansionPanelTitle(): string {
        return this.goApplicationSummaryService.getAntennaHeightTitle();
    }

    getAntennaHeightExpansionPanelDescription(): string | null {
        return this.goApplicationSummaryService.getAntennaHeightValueSummary(
            this.locationTypeControl.value,
            this.antennaHeightUnitTypeControl.value,
            this.heightToTipControl.value,
        );
    }

    getMinimumErpRequiredExpansionPanelTitle(): string {
        return this.goApplicationSummaryService.getMinimumErpRequiredTitle();
    }

    getMinimumErpRequiredExpansionPanelDescription(): string | null {
        return this.goApplicationSummaryService.getMinimumErpRequiredValueSummary(
            this.fixedErpControl.value,
            this.mobileErpControl.value,
            ', ',
        );
    }

    getEmissionDesignatorExpansionPanelTitle(): string {
        return this.goApplicationSummaryService.getEmissionDesignatorTitle();
    }

    getEmissionDesignatorExpansionPanelDescription(): string | null {
        return this.goApplicationSummaryService.getEmissionDesignatorValueSummary(
            this.emissionDesignatorPowerTypeControl.value,
            this.emissionDesignatorInformationTypeControl.value,
        );
    }

    shouldShowDataDirectionType(): boolean {
        return this.goApplicationPreparationService.isDataDirectionTypeControlRequired(
            this.locationTypeControl,
            this.stationClassTypeControl,
        );
    }

    isExclusiveStationClassOptionDisabled(): boolean {
        return this.goApplicationPreparationService.isExclusiveStationClassOptionDisabled(
            this.locationTypeControl,
        );
    }

    isHeightToTipControlDisabled(): boolean {
        return this.goApplicationPreparationService.isHeightToTipControlDisabled(
            this.locationTypeControl,
        );
    }

    isFixedErpControlDisabled(): boolean {
        return this.goApplicationPreparationService.isFixedErpControlDisabled(
            this.locationTypeControl,
        );
    }

    getExistingCoordinates(): Coordinates | null {
        const latitude = this.form.controls.latitude.value;
        const longitude = this.form.controls.longitude.value;

        return latitude != null && longitude != null
            ? {
                  latitude: parseFloat(latitude),
                  longitude: parseFloat(longitude),
              }
            : null;
    }

    onMapCoordsChange(coords: Coordinates) {
        const latitude = this.geoCoordinateSystemService.roundToPrecision(
            coords.latitude,
        );
        const longitude = this.geoCoordinateSystemService.roundToPrecision(
            coords.longitude,
        );
        this.latitudeControl.setValue(latitude.toString());
        this.longitudeControl.setValue(longitude.toString());
        this.latitudeControl.markAsDirty();
        this.longitudeControl.markAsDirty();
    }

    ngAfterViewInit(): void {
        // Depending on the state of the form, the antenna height expansion panel may need to be opened or closed,
        // so we need to update its controls accordingly.
        this.updateAntennaHeightSectionControls();

        this.updateMinimumErpSectionControls();
    }

    expandAllPanels(): void {
        this.frequencyTypeExpansionPanel.open();
        this.locationTypeExpansionPanel.open();
        this.stationClassTypeExpansionPanel.open();
        this.numberOfFrequenciesExpansionPanel.open();
        this.coordinatesExpansionPanel.open();
        if (this.antennaHeightExpansionPanel.disabled === false) {
            this.antennaHeightExpansionPanel.open();
        }
        this.erpExpansionPanel.open();
        this.emissionDesignatorExpansionPanel.open();
    }

    collapseAllPanels(): void {
        this.frequencyTypeExpansionPanel.close();
        this.locationTypeExpansionPanel.close();
        this.stationClassTypeExpansionPanel.close();
        this.numberOfFrequenciesExpansionPanel.close();
        this.coordinatesExpansionPanel.close();
        this.antennaHeightExpansionPanel.close();
        this.erpExpansionPanel.close();
        this.emissionDesignatorExpansionPanel.close();
    }

    private updateStationClassSectionControls(): void {
        this.goApplicationPreparationService.updateStationClassSectionControls(
            this.locationTypeControl,
            this.stationClassTypeControl,
        );
    }

    private updateAntennaHeightSectionControls(): void {
        this.goApplicationPreparationService.updateAntennaHeightSectionControls(
            this.locationTypeControl,
            this.antennaHeightUnitTypeControl,
            this.heightToTipControl,
        );

        if (
            this.antennaHeightUnitTypeControl.disabled === true &&
            this.heightToTipControl.disabled === true
        ) {
            this.antennaHeightExpansionPanel.close();
            this.antennaHeightExpansionPanel.disabled = true;
        } else {
            this.antennaHeightExpansionPanel.disabled = false;
            this.antennaHeightExpansionPanel.open();
        }
    }

    private updateMinimumErpSectionControls(): void {
        this.goApplicationPreparationService.updateMinimumErpSectionControls(
            this.locationTypeControl,
            this.fixedErpControl,
        );
    }

    private updateDataDirectionTypeSectionControls(): void {
        this.goApplicationPreparationService.updateDataDirectionTypeSectionControls(
            this.locationTypeControl,
            this.stationClassTypeControl,
            this.dataDirectionTypeControl,
        );
    }

    override ngOnInit(): void {
        super.ngOnInit();

        const applicationState =
            this.applicationPageFacadeService.applicationStateService.getCurrentApplicationState();

        // Load existing frequency results if applicationId is present
        this.applicationPageFacadeService.applicationStateService.applicationState$
            .pipe(
                takeUntil(this.destroy$),
                tap(() => {
                    if (applicationState?.applicationId) {
                        this.loadFromFrequencyResults(
                            applicationState.applicationId.toString(),
                        );
                    } else {
                        // If any values are cached in local storage, load them
                        const cachedForm =
                            this.goStorageService.getGoParametersFormCacheData();
                        if (cachedForm) {
                            this.form.patchValue(cachedForm, {
                                emitEvent: true,
                            });
                            this.form.updateValueAndValidity();
                        } else {
                            // Reset the form and set default values to clear any residual data
                            this.form.reset({
                                ...this.goApplicationPreparationService
                                    .controlDefaultValues,
                                applicationPurpose:
                                    this.form.controls.applicationPurpose.value,
                            });
                        }
                    }
                }),
            )
            .subscribe();

        this.form.controls.locationType.valueChanges
            .pipe(
                takeUntil(this.destroy$),
                tap(() => {
                    this.updateStationClassSectionControls();
                    this.updateAntennaHeightSectionControls();
                    this.updateMinimumErpSectionControls();

                    // Make sure controls are re-enabled and that default values are set.
                    this.refreshFormControlValuesAndValidators(this.form);
                }),
            )
            .subscribe();

        merge(
            this.form.controls.locationType.valueChanges,
            this.form.controls.stationClassType.valueChanges,
        )
            .pipe(
                takeUntil(this.destroy$),
                tap(() => {
                    this.updateDataDirectionTypeSectionControls();
                }),
            )
            .subscribe();

        // Watch for form changes to cache them
        this.form.valueChanges
            .pipe(
                debounceTime(500), // Wait 500ms after last change
                distinctUntilChanged((prev, curr) =>
                    isEqual(JSON.stringify(prev), JSON.stringify(curr)),
                ),
                takeUntil(this.destroy$),
            )
            .subscribe((formValue) => {
                this.goStorageService.setGoParametersFormCacheData(formValue);
            });
    }

    protected override shouldSubmit(): boolean {
        return false;
    }

    protected override patchFormWithGoApplicationRequestFormValue(): void {
        this.form.patchValue(this.goApplicationRequestForm.value);
    }

    protected override scrollToFirstInvalidElement(): void {
        if (this.frequencyTypeControl.invalid) {
            this.frequencyTypeExpansionPanel.open();
            this.scrollToPanel(this.frequencyTypeExpansionPanel);
        } else if (this.locationTypeControl.invalid) {
            this.locationTypeExpansionPanel.open();
            this.scrollToPanel(this.locationTypeExpansionPanel);
        } else if (
            this.stationClassTypeControl.invalid ||
            this.dataDirectionTypeControl.invalid
        ) {
            this.stationClassTypeExpansionPanel.open();
            this.scrollToPanel(this.stationClassTypeExpansionPanel);
        } else if (this.numberOfFrequenciesControl.invalid) {
            this.numberOfFrequenciesExpansionPanel.open();
            this.scrollToPanel(this.numberOfFrequenciesExpansionPanel);
        } else if (
            this.coordinatesTypeControl.invalid ||
            this.latitudeControl.invalid ||
            this.longitudeControl.invalid
        ) {
            this.coordinatesExpansionPanel.open();
            this.scrollToPanel(this.coordinatesExpansionPanel);
        } else if (this.antennaHeightUnitTypeControl.invalid) {
            this.antennaHeightExpansionPanel.open();
            this.scrollToPanel(this.antennaHeightExpansionPanel);
        } else if (
            this.fixedErpControl.invalid ||
            this.mobileErpControl.invalid
        ) {
            this.erpExpansionPanel.open();
            this.scrollToPanel(this.erpExpansionPanel);
        } else if (
            this.emissionDesignatorInformationTypeControl.invalid ||
            this.emissionDesignatorPowerTypeControl.invalid
        ) {
            this.emissionDesignatorExpansionPanel.open();
            this.scrollToPanel(this.emissionDesignatorExpansionPanel);
        }
    }

    protected override async beforeStepContinue(): Promise<boolean> {
        const asrNumber = tryParseToNumber(
            this.form.controls.towerRegistrationNumber?.value,
        );

        if (
            asrNumber &&
            this.form.controls.coordinatesType?.value === GoCoordinatesType.ASR
        ) {
            try {
                const response = await firstValueFrom(
                    this.ulsService.getASRLocation(asrNumber).pipe(
                        map((resp) => {
                            if (
                                resp &&
                                resp.latDegrees !== undefined &&
                                resp.longDegrees !== undefined
                            ) {
                                const asrLatitude =
                                    this.geoCoordinateSystemService.convertDMSToDecimal(
                                        {
                                            degrees: resp.latDegrees,
                                            minutes: resp.latMinutes,
                                            seconds: resp.latSeconds,
                                            direction: resp.latDirection,
                                        },
                                    );

                                const asrLongitude =
                                    this.geoCoordinateSystemService.convertDMSToDecimal(
                                        {
                                            degrees: resp.longDegrees,
                                            minutes: resp.longMinutes,
                                            seconds: resp.longSeconds,
                                            direction: resp.longDirection,
                                        },
                                    );

                                this.form.controls.latitude?.setValue(
                                    String(asrLatitude),
                                );
                                this.form.controls.longitude?.setValue(
                                    String(asrLongitude),
                                );
                                this.goApplicationPreparationService.patchGoApplicationRequestForm(
                                    this.form.value,
                                );
                                this.goStorageService.setGoParametersFormCacheData(
                                    this.form.value,
                                );
                                return true;
                            } else {
                                this.showAsrInvalidMsg = true;
                                return false;
                            }
                        }),
                        catchError(() => {
                            this.showAsrInvalidMsg = true;
                            return of(false);
                        }),
                    ),
                );
                return response;
            } catch {
                this.showAsrInvalidMsg = true;
                return false;
            }
        } else {
            // If ASR is not selected, clear any existing ASR number and proceed
            this.form.controls.towerRegistrationNumber?.setValue(null);
            this.goApplicationPreparationService.patchGoApplicationRequestForm(
                this.form.value,
            );
            this.goStorageService.setGoParametersFormCacheData(this.form.value);
            this.showAsrInvalidMsg = false;
            return true;
        }
    }

    private scrollToPanel(panel: MatExpansionPanel): void {
        panel._body.nativeElement.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
        });
    }

    /**
     * Loads existing Go frequency results for the given application and
     * patches the parameters form with the original request values.
     *
     * @param applicationId The ID of the Go application
     */
    private loadFromFrequencyResults(applicationId: string): void {
        this.goApplicationService
            .getParameters(applicationId)
            .pipe(
                takeUntil(this.destroy$),
                tap((parameters: GoApplicationRequest) => {
                    const formPatch =
                        this.goParametersMappingService.mapRequestToGoApplicationFormModel(
                            parameters,
                        );

                    this.form.patchValue(formPatch, { emitEvent: true });

                    this.updateStationClassSectionControls();
                    this.updateAntennaHeightSectionControls();
                    this.updateMinimumErpSectionControls();
                    this.updateDataDirectionTypeSectionControls();

                    this.form.markAsPristine();
                    this.form.updateValueAndValidity();
                }),
            )
            .subscribe();
    }
}
